import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  Button,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { utils, writeFile } from "xlsx";

// ─── API helper ───────────────────────────────────────────────────────────────

const API_BASES = Array.from(
  new Set(
    [process.env.REACT_APP_API_BASE_URL, "https://api.quvikme.com"].filter(Boolean)
  )
);

const apiFetch = async (endpoint, options = {}) => {
  const adminToken = sessionStorage.getItem("adminToken");
  const headers = {
    "Content-Type": "application/json",
    ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
    ...(options.headers || {}),
  };
  let lastErr = null;
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${endpoint}`, { ...options, headers });
      if (res.ok) return await res.json();
      lastErr = new Error(`HTTP ${res.status} from ${base}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("All endpoints failed");
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateDMY = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  } catch {
    return "—";
  }
};

const formatCurrency = (amount) =>
  amount != null ? `₹${parseFloat(amount).toFixed(2)}` : "₹0";

const getDaysRemaining = (endDate) => {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (isNaN(end)) return null;
  const diff = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : 0;
};

const toInputDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return isNaN(d) ? "" : d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const PLAN_OPTIONS   = ["starter", "basic", "standard", "premium"];
const STATUS_OPTIONS = ["active", "cancelled", "expired", "inactive"];

// ─── Edit Modal ───────────────────────────────────────────────────────────────

const EditSubscriptionModal = ({ show, subscriber, onClose, onSave }) => {
  const [form, setForm]       = useState({ type: "", status: "", price: "", end: "" });
  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  useEffect(() => {
    if (subscriber) {
      setForm({
        type:   subscriber.type   || "basic",
        status: (subscriber.status || "active").toLowerCase(),
        price:  subscriber.price  ?? "",
        end:    toInputDate(subscriber.end),
      });
      setSaveErr(null);
    }
  }, [subscriber]);

  const handle = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.end) { setSaveErr("End date is required."); return; }

    setSaving(true);
    setSaveErr(null);
    try {
      // ✅ Calls PUT /api/subscriptions/:vendorId — uses adminAuth authorization.
      //    This is the new endpoint added to subscriptions.js.
      await apiFetch(`/api/subscriptions/${subscriber.vendorId}`, {
        method: "PUT",
        body: JSON.stringify({
          subscription: {
            plan:      form.type,
            status:    form.status,
            endDate:   new Date(form.end).toISOString(),
            amount:    parseFloat(form.price) || 0,
            // preserve fields the admin didn't touch
            type:      subscriber.billing || undefined,
            startDate: subscriber.start   || undefined,
          },
        }),
      });

      // Optimistic local update
      onSave({
        ...subscriber,
        type:   form.type,
        status: form.status,
        price:  parseFloat(form.price) || 0,
        end:    new Date(form.end).toISOString(),
      });
    } catch (err) {
      setSaveErr(err.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!subscriber) return null;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Edit Subscription
          <div className="text-muted fs-6 fw-normal mt-1">{subscriber.name}</div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {saveErr && (
          <Alert variant="danger" className="py-2">
            {saveErr}
          </Alert>
        )}

        <Form>
          {/* Plan type */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Plan Type</Form.Label>
            <Form.Select name="type" value={form.type} onChange={handle}>
              {PLAN_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Status */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select name="status" value={form.status} onChange={handle}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Price & End date side-by-side */}
          <Row>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Price (₹)</Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  min={0}
                  step={0.01}
                  value={form.price}
                  onChange={handle}
                  placeholder="e.g. 349"
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="end"
                  value={form.end}
                  onChange={handle}
                />
              </Form.Group>
            </Col>
          </Row>

          {subscriber.billing && (
            <div className="text-muted small mt-1">
              Billing cycle: <strong>{subscriber.billing}</strong> (unchanged)
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Subscribers Page ─────────────────────────────────────────────────────────

const Subscribers = () => {
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [typeFilter, setTypeFilter]   = useState("all");

  const [editTarget, setEditTarget] = useState(null);
  const [showModal, setShowModal]   = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const adminToken = sessionStorage.getItem("adminToken");
        let data     = null;
        let lastErr  = null;

        for (const base of API_BASES) {
          try {
            const res = await fetch(`${base}/api/subscriptions`, {
              headers: { Authorization: `Bearer ${adminToken}` },
            });
            if (!res.ok) {
              lastErr = new Error(`${res.status} from ${base}`);
              continue;
            }
            data = await res.json();
            break;
          } catch (e) {
            lastErr = e;
          }
        }

        if (!data) throw lastErr || new Error("No data from any endpoint");
        setSubscribers(
          Array.isArray(data.subscriptions) ? data.subscriptions : []
        );
      } catch (e) {
        setError(e.message || "Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Filter ──────────────────────────────────────────────────────────────
  // Show active, non-expired vendor subscriptions only
  const filtered = subscribers
    .filter((s) => s.role?.toLowerCase() === "vendor")
    .filter((s) => (s.status || "").toLowerCase() === "active")
    .filter((s) => (s.type  || "").toLowerCase() !== "none")
    .filter((s) => {
      const end = new Date(s.end);
      return !isNaN(end) && end >= new Date();
    })
    .filter((s) =>
      typeFilter === "all" || (s.type || "").toLowerCase() === typeFilter
    );

  // ── Modal handlers ──────────────────────────────────────────────────────
  const openEdit  = (sub) => { setEditTarget(sub); setShowModal(true); };
  const closeEdit = ()    => { setShowModal(false); setEditTarget(null); };

  const handleSave = (updated) => {
    setSubscribers((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s))
    );
    closeEdit();
  };

  // ── Export ──────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filtered.map((s) => ({
      Name:         s.name,
      Plan:         s.type,
      Billing:      s.billing,
      Price:        formatCurrency(s.price),
      "Start Date": formatDateDMY(s.start),
      "End Date":   formatDateDMY(s.end),
      "Days Left":  getDaysRemaining(s.end) ?? "—",
      Status:       s.status,
    }));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rows), "Subscribers");
    writeFile(wb, "subscribers.xlsx");
  };

  // ── Guards ──────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Subscribers</h1>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            Download Excel
          </Button>
          <Form.Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ width: 180 }}
          >
            <option value="all">All Plans</option>
            {PLAN_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {/* Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            Active Vendor Subscribers ({filtered.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Vendor</th>
                <th>Plan</th>
                <th>Billing</th>
                <th>Price</th>
                <th>Start</th>
                <th>End</th>
                <th>Days Left</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-5">
                    No active subscribers found.
                  </td>
                </tr>
              ) : (
                filtered.map((s) => {
                  const days = getDaysRemaining(s.end);
                  return (
                    <tr key={s.id}>
                      <td className="align-middle">
                        <strong>{s.name}</strong>
                      </td>

                      <td className="align-middle">
                        <Badge
                          bg={
                            s.type === "premium"  ? "success"
                            : s.type === "standard" ? "info"
                            : s.type === "starter"  ? "secondary"
                            : "primary"
                          }
                        >
                          {s.type}
                        </Badge>
                      </td>

                      <td className="align-middle">{s.billing || "—"}</td>

                      <td className="align-middle">
                        <strong>{formatCurrency(s.price)}</strong>
                      </td>

                      <td className="align-middle">{formatDateDMY(s.start)}</td>
                      <td className="align-middle">{formatDateDMY(s.end)}</td>

                      <td className="align-middle">
                        {days !== null ? (
                          <Badge
                            bg={
                              days <= 3 ? "danger"
                              : days <= 7 ? "warning"
                              : "success"
                            }
                          >
                            {days} days
                          </Badge>
                        ) : "—"}
                      </td>

                      <td className="align-middle">
                        <Badge bg="success">Active</Badge>
                      </td>

                      <td className="align-middle">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEdit(s)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <EditSubscriptionModal
        show={showModal}
        subscriber={editTarget}
        onClose={closeEdit}
        onSave={handleSave}
      />
    </div>
  );
};

export default Subscribers;
